/**
 * Start, pause and finish, where a mechanic can actually hit them.
 *
 * Mechanics reported that these were buried: the only controls in the task
 * list were `size="small"` icon buttons on each service row, well under the
 * 44 px touch minimum and invisible until the card was expanded. These sit in
 * the card header, visible while the card is collapsed, and are sized for a
 * tablet in a workshop.
 *
 * They drive the **work-order level** routes, unlike the per-service buttons:
 * those move `booking_services.status` only, so pressing play left the clock
 * stopped. `/start`, `/pause` and `/resume` write `work_order_time_entries`
 * and update the services in the same transaction, so one press does what the
 * label says.
 */

import { Button, FormControl, InputLabel, MenuItem, Select, Stack, Tooltip } from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import StopIcon from '@mui/icons-material/Stop';
import { useTranslation } from 'react-i18next';
import type { BookingStatus } from '@/types';
import { workOrderTimerControls, type TimerAction } from './workOrderTimerState';

/**
 * Comfortably past the 44 px touch minimum, because this is used standing at
 * a car with a tablet. Set explicitly so the size is visible in review rather
 * than an emergent property of the button variant.
 */
const TOUCH_HEIGHT = 48;

export interface TimerServiceOption {
  bookingServiceId: string;
  name: string;
}

interface Props {
  status: BookingStatus;
  onAction: (action: TimerAction) => void;
  busy?: boolean;
  /** Office/mechanic attribution for start/resume. Blank stays unassigned (#2705). */
  services?: TimerServiceOption[];
  bookingServiceId?: string | null;
  onBookingServiceIdChange?: (bookingServiceId: string | null) => void;
}

export function WorkOrderTimerControls({
  status,
  onAction,
  busy = false,
  services,
  bookingServiceId = null,
  onBookingServiceIdChange,
}: Props) {
  const { t } = useTranslation('workorders');
  const { primary, canComplete, blockedByArrival } = workOrderTimerControls(status);

  if (primary === null) return null;

  const primaryIcon = primary === 'pause' ? <PauseIcon /> : <PlayArrowIcon />;
  const primaryLabel = t(`timer.${primary}`);

  const primaryButton = (
    <Button
      variant="contained"
      color={primary === 'pause' ? 'inherit' : 'primary'}
      size="large"
      startIcon={primaryIcon}
      disabled={blockedByArrival || busy}
      onClick={(e) => {
        // The header toggles the card; a press here must not also expand it.
        e.stopPropagation();
        onAction(primary);
      }}
      sx={{ minHeight: TOUCH_HEIGHT, px: 2.5, whiteSpace: 'nowrap' }}
      data-testid={`timer-${primary}`}
    >
      {primaryLabel}
    </Button>
  );

  const setServiceId = onBookingServiceIdChange;
  const showServiceSelect = (services?.length ?? 0) > 0
    && setServiceId != null
    && (primary === 'start' || primary === 'resume');

  return (
    <Stack direction="row" spacing={1} onClick={(e) => e.stopPropagation()} alignItems="center">
      {showServiceSelect && setServiceId && (
        <FormControl size="small" sx={{ minWidth: 160 }} disabled={blockedByArrival || busy} data-testid="timer-service">
          <InputLabel id="timer-service-label">{t('timeEntries.service')}</InputLabel>
          <Select
            labelId="timer-service-label"
            label={t('timeEntries.service')}
            value={bookingServiceId ?? ''}
            onChange={(event) => {
              const next = event.target.value;
              setServiceId(next === '' ? null : next);
            }}
            inputProps={{ 'aria-label': t('timeEntries.service') }}
          >
            <MenuItem value="">{t('timeEntries.unassigned')}</MenuItem>
            {services?.map((service) => (
              <MenuItem key={service.bookingServiceId} value={service.bookingServiceId}>
                {service.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}
      {blockedByArrival ? (
        // `span` because a disabled button swallows the events a Tooltip needs.
        <Tooltip title={t('timer.vehicleNotArrived')}>
          <span>{primaryButton}</span>
        </Tooltip>
      ) : (
        primaryButton
      )}

      {canComplete && (
        <Button
          variant="outlined"
          color="success"
          size="large"
          startIcon={<StopIcon />}
          disabled={busy}
          onClick={(e) => {
            e.stopPropagation();
            onAction('complete');
          }}
          sx={{ minHeight: TOUCH_HEIGHT, px: 2.5, whiteSpace: 'nowrap' }}
          data-testid="timer-complete"
        >
          {t('timer.complete')}
        </Button>
      )}
    </Stack>
  );
}
